import type { Claim } from '@entities/legal/types';
import { claimStatusLabel } from '@shared/lib/statusLabels';
import { AnalyzingBadge } from '@shared/ui/AnalyzingBadge';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import {
  RegistryEmpty,
  RegistryThSortable,
} from '@shared/ui/RegistryShell';
import { Skeleton } from '@shared/ui/Skeleton';
import { Table } from '@shared/ui/Table';
import { ClaimClickableRow, ClaimEmptyDash } from './styles';

interface ClaimsTableProps {
  items: Claim[];
  isLoading: boolean;
  emptyMessage: string;
  sort: string;
  order: 'asc' | 'desc';
  onSort: (field: string) => void;
  onRowClick: (id: string) => void;
}

export function ClaimsTable({
  items,
  isLoading,
  emptyMessage,
  sort,
  order,
  onSort,
  onRowClick,
}: ClaimsTableProps) {
  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <th>Тип</th>
            <th>Направление</th>
            <RegistryThSortable
              $active={sort === 'counterparty'}
              onClick={() => onSort('counterparty')}
            >
              Контрагент{sortArrow('counterparty')}
            </RegistryThSortable>
            <th>Предмет спора</th>
            <RegistryThSortable $active={sort === 'amount'} onClick={() => onSort('amount')}>
              Сумма{sortArrow('amount')}
            </RegistryThSortable>
            <th>Анализ</th>
            <RegistryThSortable $active={sort === 'stage'} onClick={() => onSort('stage')}>
              Стадия{sortArrow('stage')}
            </RegistryThSortable>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={`sk-${i}`}>
                <td>
                  <Skeleton $width={90} $height={20} $radius="999px" />
                </td>
                <td>
                  <Skeleton $width={80} $height={20} $radius="999px" />
                </td>
                <td>
                  <Skeleton $width="70%" $height={14} />
                </td>
                <td>
                  <Skeleton $width="60%" $height={14} />
                </td>
                <td>
                  <Skeleton $width={90} $height={14} />
                </td>
                <td>
                  <Skeleton $width={110} $height={20} $radius="999px" />
                </td>
                <td>
                  <Skeleton $width={120} $height={20} $radius="999px" />
                </td>
              </tr>
            ))
          ) : items.length === 0 ? (
            <tr>
              <RegistryEmpty colSpan={7}>{emptyMessage}</RegistryEmpty>
            </tr>
          ) : (
            items.map((c) => (
              <ClaimClickableRow
                key={c.id}
                onClick={() => onRowClick(c.id)}
              >
                <td>
                  <Badge>{c.claim_type === 'claim' ? 'претензия' : 'иск'}</Badge>
                </td>
                <td>
                  <Badge>{c.direction === 'outgoing' ? 'исходящая' : 'входящая'}</Badge>
                </td>
                <td>{c.counterparty}</td>
                <td>{c.dispute_type ?? '—'}</td>
                <td>{c.amount != null ? c.amount.toLocaleString('ru-RU') : '—'}</td>
                <td>
                  {c.analysis_status === 'processing' ? (
                    <AnalyzingBadge />
                  ) : c.analysis_status === 'analyzed' ? (
                    <Badge>проанализирована</Badge>
                  ) : c.analysis_status === 'failed' ? (
                    <Badge>ошибка</Badge>
                  ) : (
                    <ClaimEmptyDash>—</ClaimEmptyDash>
                  )}
                </td>
                <td>
                  <Badge>{claimStatusLabel(c.stage)}</Badge>
                </td>
              </ClaimClickableRow>
            ))
          )}
        </tbody>
      </Table>
    </Card>
  );
}
